import angular from 'angular';

import * as Configuration from 'src/ajs-app/app.constants.js';
import { CapabilityIdEnum } from 'src/app/shared/capabilities-and-permissions';

export class UsersRolesService {
  constructor($http, $q, $translate, $state, DropdownItemsFactory) {
    this.$http = $http;
    this.$q = $q;
    this.$translate = $translate;
    this.$state = $state;
    this.DropdownItemsFactory = DropdownItemsFactory;
  }

  /**
   * Allows to retrieve the users without the permissions.
   * @remarks the BE is responsible of the translations of each team and role and returns and array of `{id, label}`.
   * @returns the users
   */
  getUsersV2(companyId) {
    return this.$http.get(`${Configuration.constants.baseUrl}api/2.0/companies/${companyId}/users`);
  }

  getUserById(companyId, userId) {
    return this.$http.get(`${Configuration.constants.baseUrl}api/1.0/companies/${companyId}/users/${userId}`);
  }

  createUser(companyId, user) {
    return this.$http.post(`${Configuration.constants.baseUrl}api/1.0/companies/${companyId}/users`, user);
  }

  updateUser(companyId, user) {
    user.capabilities_by_site_id = Object.keys(user.capabilities_by_site_id).reduce((dict, siteId) => {
      if (user.capabilities_by_site_id[siteId].size) {
        dict[siteId] = Array.from(user.capabilities_by_site_id[siteId])
      }
      return dict;
    }, {});

    const userToSend = angular.copy(user);

    delete userToSend.teamList; // Delete user properties not needed by the server
    delete userToSend.sortTeams;
    delete userToSend.roleList;
    delete userToSend.sortRoles;
    delete userToSend.allPermissionsIds;
    delete userToSend.specialPermissionsIds;
    delete userToSend.permissionsIdsFromRoles;

    return this.$http.put(Configuration.constants.baseUrl + 'api/1.0/companies/' + companyId + '/users/' + userToSend.id, userToSend);
  }

  /**
   * Allows to unassign to user for the given team.
   * @param {*} companyId the company id concerned.
   * @param {*} teamId the team concerned.
   * @param {*} userId the user to unassign.
   * @returns a Promise
   */
  deleteUserFromTeam(companyId, teamId, userId) {
    return this.$http.delete(`${Configuration.constants.baseUrl}api/1.0/companies/${companyId}/users/${userId}/teams/${teamId}`);
  }

  updateUserForRole(companyId, roleId, usersIds) {
    return this.$http.post(`${Configuration.constants.baseUrl}api/1.0/companies/${companyId}/roles/${roleId}/attach`, usersIds);
  }

  /**
   * Allows to assign the user in the given team.
   * @param {*} companyId the company id concerned.
   * @param {*} teamId the team concerned.
   * @param {*} userId the user to assign.
   * @returns a Promise
   */
  addUserInTeam(companyId, teamId, userId) {
    return this.$http.post(`${Configuration.constants.baseUrl}api/1.0/companies/${companyId}/users/${userId}/teams/${teamId}`, {});
  }

  /**
   * Allows to unassign to user for the given role.
   * @param {*} companyId the company id concerned.
   * @param {*} roleId the role concerned.
   * @param {*} userId the user to unassign.
   * @returns a Promise
   */
  deleteUserFromRole(companyId, roleId, userId) {
    return this.$http.delete(`${Configuration.constants.baseUrl}api/1.0/companies/${companyId}/users/${userId}/roles/${roleId}`);
  }

  /**
 * Allows to assign the user in the given team.
 * @param {*} companyId the company id concerned.
 * @param {*} roleId the role concerned.
 * @param {*} userId the user to assign.
 * @returns a Promise
 */
  addUserInRole(companyId, roleId, userId) {
    return this.$http.post(`${Configuration.constants.baseUrl}api/1.0/companies/${companyId}/users/${userId}/roles/${roleId}`, {});
  }


  generatePassResetLink(companyId, userId) {
    return this.$http.get(Configuration.constants.baseUrl + 'api/1.0/companies/' + companyId + '/users/' + userId + '/password-confirmation-url');
  }


  // Roles
  getRoles(companyId) {
    return this.$http.get(Configuration.constants.baseUrl + 'api/1.0/companies/' + companyId + '/roles');
  }

  createRole(companyId, role) {
    return this.$http.post(Configuration.constants.baseUrl + 'api/1.0/companies/' + companyId + '/roles', role);
  }

  updateRole(companyId, role) {
    return this.$http.put(Configuration.constants.baseUrl + 'api/1.0/companies/' + companyId + '/roles/' + role.id, role);
  }

  deleteRole(companyId, role) {
    return this.$http.delete(Configuration.constants.baseUrl + 'api/1.0/companies/' + companyId + '/roles' + '/' + role.id);
  }


  // Teams
  getTeams(companyId, routingTagExists) {
    // In case of a company with no routing tag just return an empty teams array
    if (!routingTagExists) {
      return new Promise((resolve, reject) => {
        resolve({ data: [] });
      });
    }
    return this.$http.get(Configuration.constants.baseUrl + 'api/1.0/companies/' + companyId + '/teams');
  }

  createTeam(companyId, team) {
    return this.$http.post(Configuration.constants.baseUrl + 'api/1.0/companies/' + companyId + '/teams', team);
  }

  updateTeam(companyId, team) {
    team.tag_id = team.id;
    team.name = team.label;
    return this.$http.put(Configuration.constants.baseUrl + 'api/1.0/companies/' + companyId + '/teams/' + team.tag_id, team);
  }

  updateTeamsOrder(companyId, teams) {
    return this.$http.put(Configuration.constants.baseUrl + 'api/1.0/companies/' + companyId + '/teams', { 'display_order': teams.map(t => t.tag_id) });
  }

  replaceTeam(companyId, oldTeamTagId, newTeamTagId) {
    return this.$http.delete(Configuration.constants.baseUrl + 'api/1.0/companies/' + companyId + '/teams' + '/' + oldTeamTagId + '/' + newTeamTagId);
  }


  // Redirects
  redirectToUsersTeamsRoles($state, tabIndex) {
    $state.go('auth.alcmeon.fuse.users-roles', {
      'companyId': this.$state.params.companyId,
      'tabIndex': tabIndex
    });
  }

  redirectToUserEdit($event, user, newTab = false) {
    if (newTab) {
      const url = Configuration.constants.frontendBaseUrl + this.$state.href('auth.alcmeon.fuse.user-edit', {
        companyId: this.$state.params.companyId,
        id: user.id
      });

      window.open(url, '_blank');
    } else {
      this.$state.go('auth.alcmeon.fuse.user-edit', {
        companyId: this.$state.params.companyId,
        id: user.id
      });
    }
  }

  redirectToRoleEdit($event, role, trackTable, user) {
    this.$state.go('auth.alcmeon.fuse.role-edit', {
      'companyId': this.$state.params.companyId,
      'role': role,
      'trackTable': trackTable,
      'user': user,
    });
  }

  redirectToTeamEdit($event, team, trackTable, user) {
    this.$state.go('auth.alcmeon.fuse.team-edit', {
      'companyId': this.$state.params.companyId,
      'team': team,
      'trackTable': trackTable,
      'user': user
    });
  }


  // Helpers
  // Custom sort sites or roles - sort alphabetically by name 
  sortUsersSitesRoles(items, sortKey) {
    items.sort((x, y) => {
      if (x[sortKey] < y[sortKey]) {
        return -1;
      }
      if (x[sortKey] > y[sortKey]) {
        return 1;
      }
      return 0;
    });
  }

  getPermissionsTooltips() {

    const translate = (key) => {
      return this.$translate.instant(`users_roles/tooltips/permissions/${key}`);
    }

    const tooltips = {
      [CapabilityIdEnum.Answer]: translate('answer'),
      [CapabilityIdEnum.Actions]: translate('actions'),
      [CapabilityIdEnum.Statistics]: translate('statistics'),
      [CapabilityIdEnum.Publish]: translate('publish'),
      [CapabilityIdEnum.ProcessHelpRequests]: translate('process-help-requests'),
      [CapabilityIdEnum.Route]: translate('route'),
      [CapabilityIdEnum.TagVIP]: translate('tag-vip'),
      [CapabilityIdEnum.Automation]: translate('automation'),
      [CapabilityIdEnum.Marketing]: translate('marketing'),
      [CapabilityIdEnum.ReadOnly]: translate('read-only'),
      [CapabilityIdEnum.Engagement]: translate('engagement'),
    };

    return tooltips;
  }

  static UsersRolesFactory($http, $q, $translate, $state, DropdownItemsFactory) {
    return new UsersRolesService($http, $q, $translate, $state, DropdownItemsFactory);
  }
}

UsersRolesService.UsersRolesFactory.$inject = ['$http', '$q', '$translate', '$state', 'DropdownItemsFactory'];
